import { Popover, PopoverButton, PopoverPanel, Transition } from "@headlessui/react";
import { Fragment, useContext, useRef } from "react";
import { AppStateContext } from "../../state/AppProvider";
import { ButtonTooltip } from "../ButtonTooltip";
import { Assistant } from "../../api";
import { FaPlus, FaCog, FaCheck } from "react-icons/fa";

interface Props {
    onAssistantSelected: () => void;
    windowWidth: number;
    setIsAssistantDialogOpen: (open: boolean) => void;
    setAssistantDialogTab: (tab: "manage" | "create") => void;
}

interface AssistantSection {
    label: string | null;
    items: Assistant[];
}

interface PanelContentProps {
    sections: AssistantSection[];
    selectedId: string | undefined;
    canCreateAssistants: boolean;
    onSelect: (assist: Assistant) => void;
    onManage: () => void;
    onCreate: () => void;
    close: () => void;
}

const AssistantPanelContent = ({ sections, selectedId, canCreateAssistants, onSelect, onManage, onCreate, close }: PanelContentProps) => {
    const listRef = useRef<HTMLDivElement>(null);

    const moveItemFocus = (offset: number) => {
        const items = Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>("[data-assistant-item]:not(:disabled)") ?? []);
        if (items.length === 0) return;
        const current = items.indexOf(document.activeElement as HTMLButtonElement);
        const next = current === -1 ? (offset > 0 ? 0 : items.length - 1) : Math.min(Math.max(current + offset, 0), items.length - 1);
        items[next]?.focus();
    };

    const onPanelKeyDown = (ev: React.KeyboardEvent) => {
        if (ev.key === "ArrowDown") {
            ev.preventDefault();
            moveItemFocus(1);
        } else if (ev.key === "ArrowUp") {
            ev.preventDefault();
            moveItemFocus(-1);
        }
    };

    // max-w lives here, not on the panel: the anchored panel's inline maxWidth
    // would override a class there.
    return (
        <div className="flex min-h-0 max-w-[22rem] flex-col" onKeyDown={onPanelKeyDown}>
            <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                {sections.map(section => (
                    <Fragment key={section.label ?? "all"}>
                        {section.label && (
                            <div className="sticky top-0 bg-white px-3 pt-2 pb-1 text-xs font-medium uppercase text-gray-500">{section.label}</div>
                        )}
                        {section.items.map(assist => {
                            const isDisabled = assist.available === false;
                            const isSelected = assist.id === selectedId;

                            return (
                                <ButtonTooltip
                                    key={assist.id}
                                    title={isDisabled ? "Assistant is learning... check back later" : ""}
                                    placement="right"
                                    disableHoverListener={!isDisabled}
                                    disableFocusListener={!isDisabled}
                                    disableTouchListener={!isDisabled}
                                    className="ButtonTooltip"
                                >
                                    <span className="block">
                                        <button
                                            data-assistant-item
                                            title={assist.title}
                                            className={`group flex w-full items-center gap-2 rounded-lg py-1.5 px-3 text-left font-semibold tracking-[.15rem]
                        ${isDisabled ? "text-gray-400 cursor-not-allowed" : isSelected ? "text-jtc-blue" : "hover:text-jtc-blue"}
                      `}
                                            onClick={() => {
                                                if (!isDisabled) {
                                                    onSelect(assist);
                                                    close();
                                                }
                                            }}
                                            disabled={isDisabled}
                                        >
                                            <span className="truncate">{assist.title}</span>
                                            {isSelected && <FaCheck className="ml-auto h-3 w-3 shrink-0" />}
                                        </button>
                                    </span>
                                </ButtonTooltip>
                            );
                        })}
                    </Fragment>
                ))}
            </div>

            {/* Create and manage assistants — pinned below the scroll area.
                Manage is open to everyone (the backend scopes it to assistants
                the user manages); Create stays behind the builder group. */}
            <div className="shrink-0">
                <div style={{ backgroundColor: "#65216e", height: "1px", margin: "8px 0" }} />

                <button
                    className="group flex w-full items-center gap-2 rounded-lg py-1.5 px-3 hover:text-jtc-blue font-semibold tracking-[.15rem]"
                    onClick={() => {
                        close();
                        onManage();
                    }}
                >
                    <FaCog className="w-2 h-3 text-current group-hover:text-jtc-blue" />
                    Manage Assistants
                </button>

                {canCreateAssistants && (
                    <button
                        className="group flex w-full items-center gap-2 rounded-lg py-1.5 px-3 hover:text-jtc-blue font-semibold tracking-[.15rem]"
                        onClick={() => {
                            close();
                            onCreate();
                        }}
                    >
                        <FaPlus className="w-2 h-3 text-current group-hover:text-jtc-blue" />
                        Create Custom Assistant
                    </button>
                )}
            </div>
        </div>
    );
};

export const ModelSelector = ({
    onAssistantSelected,
    windowWidth,
    setIsAssistantDialogOpen,
    setAssistantDialogTab
}: Props) => {
    const appStateContext = useContext(AppStateContext);
    const canCreateAssistants = appStateContext?.state.canCreateAssistants ?? false;

    const onAssistantClicked = (assist: Assistant) => {
        appStateContext?.dispatch({ type: "UPDATE_ASSISTANT", payload: assist });
        onAssistantSelected();
    };

    const onCreateAssistantClicked = () => {
        setAssistantDialogTab("create");
        setIsAssistantDialogOpen(true);
    };

    const onManageAssistantsClicked = () => {
        setAssistantDialogTab("manage");
        setIsAssistantDialogOpen(true);
    };

    // Filtering the full list (rather than concatenating the categorized state
    // arrays) keeps the backend's title ordering across owned + shared customs.
    const allAssistants = appStateContext?.state.assistants ?? [];
    const jtcAssistants = allAssistants.filter(a => a.type !== "custom");
    const customAssistants = allAssistants.filter(a => a.type === "custom");

    // No custom assistants: keep the flat headerless list.
    const sections: AssistantSection[] =
        customAssistants.length > 0
            ? [
                { label: "JTC Assistants", items: jtcAssistants },
                { label: "Custom Assistants", items: customAssistants }
            ]
            : [{ label: null, items: allAssistants }];

    return (
        <Popover>
            <PopoverButton className="group flex cursor-pointer items-center text-jtc-white overflow-hidden whitespace-nowrap tracking-[.15rem] hover:bg-jtc-purple px-2.5 h-[40px] rounded-lg">
                <div className="after:content-['›'] after:inline-block after:ml-2.5 after:rotate-90">
                    {appStateContext?.state.assistant?.short_title}
                </div>
            </PopoverButton>
            <Transition
                enter="transition ease-out duration-75"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
            >
                {/* Height/spacing via --anchor-* vars: Headless UI sets an inline
                    maxHeight of min(--anchor-max-height, space-to-viewport-edge),
                    which overrides any max-h class. --anchor-gap replaces the old
                    translate-y-1, which pushed the panel past the viewport bottom
                    and gave the page a scrollbar. */}
                <PopoverPanel
                    anchor={windowWidth >= 976 ? "bottom start" : "bottom"}
                    className="flex flex-col min-w-[14rem] origin-top-right rounded-lg border bg-white p-1 text-sm text-jtc-purple focus:outline-none [--anchor-gap:4px] [--anchor-padding:8px] [--anchor-max-height:32rem]"
                >
                    {({ close }) => (
                        <AssistantPanelContent
                            sections={sections}
                            selectedId={appStateContext?.state.assistant?.id}
                            canCreateAssistants={canCreateAssistants}
                            onSelect={onAssistantClicked}
                            onManage={onManageAssistantsClicked}
                            onCreate={onCreateAssistantClicked}
                            close={() => close()}
                        />
                    )}
                </PopoverPanel>
            </Transition>
        </Popover>
    );
};
